/**
 * Authentication initialization logic for router beforeLoad
 */

import {
	clearAllAuthData,
	createAuthFlow,
	getOAuthTokensFromStorage,
	hasAuthToken,
	hasOAuthTokensInStorage,
	saveAuthenticationTokens,
} from "@medme/auth"
import * as Sentry from "@sentry/react"
import type { QueryClient } from "@tanstack/react-query"
import { getAppConfig } from "@/app/config/app-config"
import { customFetch as customOAuthFetch } from "@/shared/api/custom-oauth-client"
import {
	exchangeAuthCode,
	getExchangeAuthCodeMutationOptions,
} from "@/shared/api/generated/oauth/oauth-api"
import { ExchangeAuthCodeResponse } from "@/shared/api/generated/oauth/oauth-api.zod"
import { getOrganizationslistOrganizationsQueryOptions } from "@/shared/api/generated/organizations/organizations"
import { getPlatformAdminslistPlatformAdminsQueryOptions } from "@/shared/api/generated/platform-admins/platform-admins"
import { validateSuccessResponse } from "@/shared/lib/zod-validation-helpers"
import type { AuthState, InitAuthParams } from "./types"

export type PlatformAccessGate = "ok" | "denied"

const httpStatus = (error: unknown): number | null => {
	if (
		typeof error === "object" &&
		error !== null &&
		"status" in error &&
		typeof error.status === "number"
	) {
		return error.status
	}
	return null
}

/**
 * A live JWT is not enough: corev2 must accept the caller as a platform admin.
 * GET /api/platform-admins returns 200 for platform_access and superuser, 403 otherwise.
 * Any other failure also keeps the app closed.
 */
export async function confirmPlatformAccess(
	queryClient: QueryClient,
): Promise<PlatformAccessGate> {
	try {
		const list = await queryClient.fetchQuery(
			getPlatformAdminslistPlatformAdminsQueryOptions({
				query: { retry: false },
			}),
		)
		return list.status === 200 ? "ok" : "denied"
	} catch (error) {
		const status = httpStatus(error)
		if (status === 401 || status === 403) return "denied"
		return "denied"
	}
}

const { establishAuthSession, refreshAuthTokens, logout } =
	createAuthFlow(customOAuthFetch)

/**
 * Initialize authentication state
 * Handles OAuth code exchange, token refresh, and JWT authentication
 *
 * Initialized at root level in beforeLoad. On page preload will check
 * organization-api access, otherwise refreshes tokens via oauth & authenticates.
 */
export async function initAuthAsync({
	oauthCode,
	queryClient,
}: InitAuthParams): Promise<AuthState> {
	// Check if JWT token already exists
	if (await hasAuthToken()) {
		return finishPlatformAccess(queryClient)
	}

	// Handle OAuth code from URL
	if (oauthCode) {
		try {
			const exchangeResponse = await queryClient.fetchQuery({
				queryKey: getExchangeAuthCodeMutationOptions().mutationKey ?? [],
				queryFn: () =>
					exchangeAuthCode({
						client_id: getAppConfig().clientId,
						code: oauthCode,
						redirect_uri: window.location.origin + window.location.pathname,
					}),
			})

			const exchangeTokens = validateSuccessResponse(
				ExchangeAuthCodeResponse,
				exchangeResponse,
			)

			if (!exchangeTokens) {
				throw new Error("Exchange tokens not found in exchange response")
			}

			// Sets JWT token in cookie через OAuth-сервер (Domain=.${DOMAIN})
			await establishAuthSession(exchangeTokens.user, exchangeTokens.token)

			// Save all tokens. Token already saved to cookie by previous call.
			saveAuthenticationTokens(exchangeTokens)
			localStorage.setItem("user_id", exchangeTokens.user)

			Sentry.addBreadcrumb({
				category: "auth",
				message: "OAuth authentication successful",
				level: "info",
			})

			return finishPlatformAccess(queryClient)
		} catch (err) {
			const error =
				err instanceof Error ? err : new Error("OAuth authentication failed")
			Sentry.captureException(error, {
				tags: { category: "auth", operation: "oauth_exchange" },
				extra: { masked: true },
			})
			await clearAllAuthData()
			return createAuthState(false, error)
		}
	}

	// Check if OAuth tokens exist in storage
	if (hasOAuthTokensInStorage()) {
		// Проверка авторизации через organization-api: любой валидный
		// ответ (не 401) на список организаций означает, что JWT рабочий.
		try {
			const organizations = await queryClient.ensureQueryData(
				getOrganizationslistOrganizationsQueryOptions(undefined, {
					query: { retry: 1 },
				}),
			)
			if (organizations.status !== 401) {
				return finishPlatformAccess(queryClient)
			}
		} catch (error) {
			console.error("Error checking organization-api access:", error)
			Sentry.captureException(error, {
				tags: { category: "auth", operation: "check_organization_access" },
			})
		}

		const refreshResult = await refreshAuthTokens()

		if (refreshResult) {
			saveAuthenticationTokens({
				token: refreshResult.access_token,
				refreshToken: refreshResult.refresh_token,
			})

			Sentry.addBreadcrumb({
				category: "auth",
				message: "JWT authentication successful via token refresh",
				level: "info",
			})

			return finishPlatformAccess(queryClient)
		}

		// If refresh failed, try regular authentication
		try {
			const storedTokens = getOAuthTokensFromStorage()
			const userId = localStorage.getItem("user_id")
			if (!storedTokens || !userId) {
				return createAuthState(false)
			}

			// Sets JWT token in cookie через OAuth-сервер (Domain=.${DOMAIN})
			await establishAuthSession(userId, storedTokens.token)

			Sentry.addBreadcrumb({
				category: "auth",
				message: "JWT authentication successful",
				level: "info",
			})

			return finishPlatformAccess(queryClient)
		} catch (err) {
			const error =
				err instanceof Error ? err : new Error("JWT authentication failed")
			Sentry.captureException(error, {
				tags: { category: "auth", operation: "jwt_auth" },
				extra: { masked: true },
			})
			await clearAllAuthData()
			return createAuthState(false, error)
		}
	}

	return createAuthState(false)
}

async function finishPlatformAccess(
	queryClient: QueryClient,
): Promise<AuthState> {
	const gate = await confirmPlatformAccess(queryClient)
	if (gate === "ok") return createAuthState(true)
	await clearAllAuthData()
	return createAuthState(false)
}

/**
 * Create authentication state object
 */
function createAuthState(
	isAuthenticated: boolean,
	error: Error | null = null,
): AuthState {
	return {
		isAuthenticated,
		isAuthenticating: false,
		error,
		logout: () => {
			logout(getAppConfig().clientId).finally(() => {
				window.location.reload()
			})
		},
	}
}
